import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Steps,
  Row,
  Col,
  Descriptions,
  Tag,
  Timeline,
  Upload,
  Tabs,
  Alert,
  InputNumber,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  EyeOutlined,
  UploadOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_OUTSOURCING_ORDER } from '@/constants/document-entity-types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface OutsourcingActivity {
  id: string;
  activity_code: string;
  activity_name: string;
  activity_category: string;
  requires_procedure_approval: boolean;
  requires_witness_inspection: boolean;
  risk_level: string;
}

interface OutsourcingOrder {
  id: string;
  order_number: string;
  supplier_name: string;
  activity_name: string;
  order_date: string;
  required_date: string;
  status: string;
  procedure_approval_status: string;
  witness_inspection_status: string;
  quantity: number;
  unit_of_measure: string;
}

const OutsourcingManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<OutsourcingActivity[]>([]);
  const [orders, setOrders] = useState<OutsourcingOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [procedureModalVisible, setProcedureModalVisible] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [inspectionModalVisible, setInspectionModalVisible] = useState(false);
  const [recordInspectionModalVisible, setRecordInspectionModalVisible] = useState(false);

  const [createForm] = Form.useForm();
  const [procedureForm] = Form.useForm();
  const [reviewForm] = Form.useForm();
  const [inspectionForm] = Form.useForm();
  const [recordInspectionForm] = Form.useForm();

  useEffect(() => {
    fetchActivities();
    fetchOrders();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await apiClient.get('/procurement/outsourcing-activities');
      setActivities(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch activities');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/procurement/outsourcing-orders');
      setOrders(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (id: string) => {
    try {
      const response = await apiClient.get(`/procurement/outsourcing-orders/${id}`);
      setSelectedOrder(response.data.data);
      setDetailsModalVisible(true);
    } catch (error) {
      message.error('Failed to fetch order details');
    }
  };

  const handleCreateOrder = async (values: any) => {
    try {
      await apiClient.post('/procurement/outsourcing-orders', {
        po_id: values.po_id,
        work_order_id: values.work_order_id,
        item_id: values.item_id,
        supplier_id: values.supplier_id,
        supplier_name: values.supplier_name,
        activity_id: values.activity_id,
        activity_name: activities.find(a => a.id === values.activity_id)?.activity_name,
        activity_description: values.activity_description,
        order_date: values.order_date.format('YYYY-MM-DD'),
        required_date: values.required_date.format('YYYY-MM-DD'),
        quantity: values.quantity,
        unit_of_measure: values.unit_of_measure,
        technical_specifications: values.technical_specifications,
        quality_requirements: values.quality_requirements,
        applicable_codes: values.applicable_codes,
        estimated_cost: values.estimated_cost,
        currency_code: values.currency_code || 'AED',
      });
      message.success('Outsourcing order created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchOrders();
    } catch (error) {
      message.error('Failed to create order');
    }
  };

  const handleSubmitProcedure = async (values: any) => {
    if (!selectedOrder) return;

    try {
      await apiClient.post(
        `/procurement/outsourcing-orders/${selectedOrder.id}/submit-procedure`,
        {
          procedure_document_name: values.procedure_document_name,
          procedure_revision: values.procedure_revision,
          document_url: values.document_url,
          submitted_date: values.submitted_date.format('YYYY-MM-DD'),
          review_required_by: values.review_required_by?.format('YYYY-MM-DD'),
        }
      );
      message.success('Procedure submitted for approval');
      setProcedureModalVisible(false);
      procedureForm.resetFields();
      fetchOrderDetails(selectedOrder.id);
    } catch (error) {
      message.error('Failed to submit procedure');
    }
  };

  const handleReviewProcedure = async (values: any, approvalId: string) => {
    try {
      await apiClient.post(`/procurement/procedure-approvals/${approvalId}/review`, {
        review_status: values.review_status,
        review_findings: values.review_findings,
        compliance_issues: values.compliance_issues,
        corrective_actions_required: values.corrective_actions_required,
      });
      message.success('Procedure reviewed successfully');
      setReviewModalVisible(false);
      reviewForm.resetFields();
      if (selectedOrder) {
        fetchOrderDetails(selectedOrder.id);
      }
      fetchOrders();
    } catch (error) {
      message.error('Failed to review procedure');
    }
  };

  const handleScheduleInspection = async (values: any) => {
    if (!selectedOrder) return;

    try {
      await apiClient.post(
        `/procurement/outsourcing-orders/${selectedOrder.id}/schedule-inspection`,
        {
          inspection_type: values.inspection_type,
          inspection_stage: values.inspection_stage,
          inspection_date: values.inspection_date.format('YYYY-MM-DD'),
          inspector_id: values.inspector_id,
          inspector_name: values.inspector_name,
          inspection_scope: values.inspection_scope,
          acceptance_criteria: values.acceptance_criteria,
        }
      );
      message.success('Inspection scheduled successfully');
      setInspectionModalVisible(false);
      inspectionForm.resetFields();
      fetchOrderDetails(selectedOrder.id);
    } catch (error) {
      message.error('Failed to schedule inspection');
    }
  };

  const handleRecordInspectionResults = async (values: any, inspectionId: string) => {
    try {
      await apiClient.post(
        `/procurement/outsourcing-inspections/${inspectionId}/record-results`,
        {
          inspection_result: values.inspection_result,
          findings: values.findings,
          non_conformances: values.non_conformances,
          observations: values.observations,
          supplier_representative: values.supplier_representative,
          inspection_report_url: values.inspection_report_url,
        }
      );
      message.success('Inspection results recorded successfully');
      setRecordInspectionModalVisible(false);
      recordInspectionForm.resetFields();
      if (selectedOrder) {
        fetchOrderDetails(selectedOrder.id);
      }
      fetchOrders();
    } catch (error) {
      message.error('Failed to record inspection results');
    }
  };

  const handleCompleteOrder = async (orderId: string, completedQuantity: number, actualCost: number) => {
    try {
      await apiClient.post(`/procurement/outsourcing-orders/${orderId}/complete`, {
        completed_quantity: completedQuantity,
        actual_cost: actualCost,
      });
      message.success('Order completed successfully');
      if (selectedOrder) {
        fetchOrderDetails(selectedOrder.id);
      }
      fetchOrders();
    } catch (error) {
      message.error('Failed to complete order');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: { [key: string]: { color: string; icon: React.ReactNode } } = {
      Draft: { color: 'default', icon: <ClockCircleOutlined /> },
      Sent: { color: 'blue', icon: <FileTextOutlined /> },
      'In-Progress': { color: 'processing', icon: <SyncOutlined spin /> },
      Completed: { color: 'green', icon: <CheckCircleOutlined /> },
      Cancelled: { color: 'red', icon: <CloseCircleOutlined /> },
    };
    const config = statusConfig[status] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const getApprovalStatusTag = (status: string) => {
    const statusConfig: { [key: string]: { color: string } } = {
      Pending: { color: 'orange' },
      Approved: { color: 'green' },
      Rejected: { color: 'red' },
      'Revision-Required': { color: 'blue' },
      'Not-Required': { color: 'default' },
    };
    const config = statusConfig[status] || { color: 'default' };
    return <Tag color={config.color}>{status}</Tag>;
  };

  const getRiskLevelTag = (level: string) => {
    const colors: { [key: string]: string } = {
      High: 'red',
      Medium: 'orange',
      Low: 'green',
    };
    return <Tag color={colors[level] || 'default'}>{level}</Tag>;
  };

  const getCurrentStep = (order: any) => {
    if (order.status === 'Completed') return 4;
    if (order.witness_inspection_status === 'Completed') return 3;
    if (order.procedure_approval_status === 'Approved') return 2;
    if (order.procedure_approval_status === 'Pending') return 1;
    return 0;
  };

  const columns: ColumnsType<OutsourcingOrder> = [
    {
      title: 'Order No.',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 150,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
    },
    {
      title: 'Activity',
      dataIndex: 'activity_name',
      key: 'activity_name',
      width: 200,
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      width: 120,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Required Date',
      dataIndex: 'required_date',
      key: 'required_date',
      width: 120,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: 100,
      render: (_, record) => `${record.quantity} ${record.unit_of_measure}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Procedure',
      dataIndex: 'procedure_approval_status',
      key: 'procedure_approval_status',
      width: 120,
      render: (status) => getApprovalStatusTag(status),
    },
    {
      title: 'Inspection',
      dataIndex: 'witness_inspection_status',
      key: 'witness_inspection_status',
      width: 120,
      render: (status) => getApprovalStatusTag(status),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => fetchOrderDetails(record.id)}
          >
            Details
          </Button>
        </Space>
      ),
    },
  ];

  const activitiesColumns: ColumnsType<OutsourcingActivity> = [
    {
      title: 'Activity Name',
      dataIndex: 'activity_name',
      key: 'activity_name',
    },
    {
      title: 'Category',
      dataIndex: 'activity_category',
      key: 'activity_category',
      render: (category) => <Tag>{category}</Tag>,
    },
    {
      title: 'Risk Level',
      dataIndex: 'risk_level',
      key: 'risk_level',
      render: (level) => getRiskLevelTag(level),
    },
    {
      title: 'Procedure Required',
      dataIndex: 'requires_procedure_approval',
      key: 'requires_procedure_approval',
      render: (required) => (required ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Witness Inspection',
      dataIndex: 'requires_witness_inspection',
      key: 'requires_witness_inspection',
      render: (required) => (required ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Outsourcing Control</h1>
          <p style={{ color: '#8c8c8c' }}>
            Manage outsourced activities, procedure approvals, and witness inspections
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          Create Outsourcing Order
        </Button>
      </div>

      <Tabs defaultActiveKey="1">
        <TabPane tab={`Outsourcing Orders (${orders.length})`} key="1">
          <Card>
            <Table
              columns={columns}
              dataSource={orders}
              rowKey="id"
              loading={loading}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
              scroll={{ x: 1400 }}
            />
          </Card>
        </TabPane>

        <TabPane tab={`Activities (${activities.length})`} key="2">
          <Card>
            <Table
              columns={activitiesColumns}
              dataSource={activities}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Create Order Modal */}
      <Modal
        title="Create Outsourcing Order"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        width={800}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateOrder}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="activity_id"
                label="Activity"
                rules={[{ required: true, message: 'Please select activity' }]}
              >
                <Select placeholder="Select Activity">
                  {activities.map((activity) => (
                    <Option key={activity.id} value={activity.id}>
                      {activity.activity_name} ({activity.activity_category})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="supplier_name"
                label="Supplier"
                rules={[{ required: true, message: 'Please enter supplier' }]}
              >
                <Input placeholder="Supplier Name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="order_date"
                label="Order Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="required_date"
                label="Required Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="unit_of_measure"
                label="Unit"
                rules={[{ required: true, message: 'Please enter unit' }]}
              >
                <Input placeholder="e.g., kg, pcs, hours" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="estimated_cost" label="Estimated Cost">
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency_code" label="Currency" initialValue="AED">
                <Select>
                  <Option value="AED">AED</Option>
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="technical_specifications" label="Technical Specifications">
            <TextArea rows={3} placeholder="Enter technical specifications..." />
          </Form.Item>

          <Form.Item name="quality_requirements" label="Quality Requirements">
            <TextArea rows={3} placeholder="Enter quality requirements..." />
          </Form.Item>

          <Form.Item name="applicable_codes" label="Applicable Codes/Standards">
            <Input placeholder="e.g., ASME, AWS, ISO" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Order Details Modal */}
      <Modal
        title={`Order Details: ${selectedOrder?.order_number || ''}`}
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedOrder(null);
        }}
        width={1200}
        footer={null}
      >
        {selectedOrder && (
          <>
            <Steps current={getCurrentStep(selectedOrder)} style={{ marginBottom: 24 }}>
              <Step title="Order Created" description="Draft" />
              <Step title="Procedure Submitted" description="Supplier submits procedure" />
              <Step title="Procedure Approved" description="QHSE approval" />
              <Step title="Inspection Completed" description="Witness inspection" />
              <Step title="Order Completed" description="Final completion" />
            </Steps>

            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Activity">{selectedOrder.activity_name}</Descriptions.Item>
              <Descriptions.Item label="Supplier">{selectedOrder.supplier_name}</Descriptions.Item>
              <Descriptions.Item label="Status">{getStatusTag(selectedOrder.status)}</Descriptions.Item>
              <Descriptions.Item label="Risk Level">{getRiskLevelTag(selectedOrder.risk_level)}</Descriptions.Item>
              <Descriptions.Item label="Order Date">
                {dayjs(selectedOrder.order_date).format('DD MMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Required Date">
                {dayjs(selectedOrder.required_date).format('DD MMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Quantity">
                {selectedOrder.quantity} {selectedOrder.unit_of_measure}
              </Descriptions.Item>
              <Descriptions.Item label="Estimated Cost">
                {selectedOrder.estimated_cost ? `${selectedOrder.currency_code} ${selectedOrder.estimated_cost}` : '-'}
              </Descriptions.Item>
            </Descriptions>

            <Tabs defaultActiveKey="1">
              <TabPane tab="Procedure Approvals" key="1">
                {selectedOrder.procedure_approval_required && (
                  <>
                    <Space style={{ marginBottom: 16 }}>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setProcedureModalVisible(true)}
                        disabled={selectedOrder.procedure_approval_status === 'Approved'}
                      >
                        Submit Procedure
                      </Button>
                    </Space>

                    <Timeline>
                      {selectedOrder.procedure_approvals?.map((approval: any) => (
                        <Timeline.Item
                          key={approval.id}
                          color={
                            approval.review_status === 'Approved'
                              ? 'green'
                              : approval.review_status === 'Rejected'
                              ? 'red'
                              : 'blue'
                          }
                        >
                          <p>
                            <strong>{approval.procedure_document_name}</strong> ({approval.procedure_revision})
                          </p>
                          <p>Status: {getApprovalStatusTag(approval.review_status)}</p>
                          <p>Submitted: {dayjs(approval.submitted_date).format('DD MMM YYYY')}</p>
                          {approval.review_findings && <p>Findings: {approval.review_findings}</p>}
                          {approval.review_status === 'Pending' && (
                            <Button
                              size="small"
                              type="link"
                              onClick={() => {
                                reviewForm.setFieldsValue({ approvalId: approval.id });
                                setReviewModalVisible(true);
                              }}
                            >
                              Review
                            </Button>
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </>
                )}
              </TabPane>

              <TabPane tab="Witness Inspections" key="2">
                {selectedOrder.witness_inspection_required && (
                  <>
                    <Space style={{ marginBottom: 16 }}>
                      <Button
                        type="primary"
                        icon={<CalendarOutlined />}
                        onClick={() => setInspectionModalVisible(true)}
                        disabled={
                          selectedOrder.procedure_approval_status !== 'Approved' ||
                          selectedOrder.status === 'Completed'
                        }
                      >
                        Schedule Inspection
                      </Button>
                    </Space>

                    <Timeline>
                      {selectedOrder.inspections?.map((inspection: any) => (
                        <Timeline.Item
                          key={inspection.id}
                          color={
                            inspection.inspection_result === 'Accepted'
                              ? 'green'
                              : inspection.inspection_result === 'Rejected'
                              ? 'red'
                              : 'blue'
                          }
                        >
                          <p>
                            <strong>{inspection.inspection_number}</strong> ({inspection.inspection_type})
                          </p>
                          <p>Inspector: {inspection.inspector_name}</p>
                          <p>Date: {dayjs(inspection.inspection_date).format('DD MMM YYYY')}</p>
                          {inspection.inspection_result && (
                            <>
                              <p>Result: {getStatusTag(inspection.inspection_result)}</p>
                              <p>Findings: {inspection.findings}</p>
                            </>
                          )}
                          {!inspection.inspection_result && (
                            <Button
                              size="small"
                              type="link"
                              onClick={() => {
                                recordInspectionForm.setFieldsValue({ inspectionId: inspection.id });
                                setRecordInspectionModalVisible(true);
                              }}
                            >
                              Record Results
                            </Button>
                          )}
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </>
                )}
              </TabPane>

              <TabPane tab="MTC Links" key="3">
                {selectedOrder.mtc_links?.length > 0 ? (
                  <Table
                    dataSource={selectedOrder.mtc_links}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      { title: 'MTC Number', dataIndex: 'mtc_number', key: 'mtc_number' },
                      { title: 'Material', dataIndex: 'material_description', key: 'material_description' },
                      { title: 'Heat Number', dataIndex: 'heat_number', key: 'heat_number' },
                      {
                        title: 'Status',
                        dataIndex: 'verification_status',
                        key: 'verification_status',
                        render: (status) => getApprovalStatusTag(status),
                      },
                    ]}
                  />
                ) : (
                  <Alert message="No MTC records linked" type="info" />
                )}
              </TabPane>
            </Tabs>

            <ProcurementAttachmentsCard
              entityType={DOCUMENT_ENTITY_TYPE_OUTSOURCING_ORDER}
              entityId={selectedOrder.id}
              queryKeyPrefix={['documents', 'outsourcing-order']}
            />
          </>
        )}
      </Modal>

      {/* Submit Procedure Modal */}
      <Modal
        title="Submit Supplier Procedure"
        open={procedureModalVisible}
        onCancel={() => {
          setProcedureModalVisible(false);
          procedureForm.resetFields();
        }}
        onOk={() => procedureForm.submit()}
      >
        <Form form={procedureForm} layout="vertical" onFinish={handleSubmitProcedure}>
          <Form.Item
            name="procedure_document_name"
            label="Procedure Document Name"
            rules={[{ required: true, message: 'Please enter document name' }]}
          >
            <Input placeholder="e.g., Welding Procedure Specification" />
          </Form.Item>

          <Form.Item name="procedure_revision" label="Revision">
            <Input placeholder="e.g., Rev 1.0" />
          </Form.Item>

          <Form.Item
            name="document_url"
            label="Document URL"
            rules={[{ required: true, message: 'Please enter document URL' }]}
          >
            <Input placeholder="URL to procedure document" />
          </Form.Item>

          <Form.Item
            name="submitted_date"
            label="Submitted Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="review_required_by" label="Review Required By">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Review Procedure Modal */}
      <Modal
        title="Review Supplier Procedure"
        open={reviewModalVisible}
        onCancel={() => {
          setReviewModalVisible(false);
          reviewForm.resetFields();
        }}
        onOk={() => {
          const approvalId = reviewForm.getFieldValue('approvalId');
          reviewForm.validateFields().then(values => {
            handleReviewProcedure(values, approvalId);
          });
        }}
      >
        <Form form={reviewForm} layout="vertical">
          <Form.Item name="approvalId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="review_status"
            label="Review Decision"
            rules={[{ required: true, message: 'Please select decision' }]}
          >
            <Radio.Group>
              <Radio value="Approved">Approved</Radio>
              <Radio value="Rejected">Rejected</Radio>
              <Radio value="Revision-Required">Revision Required</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="review_findings" label="Review Findings">
            <TextArea rows={4} placeholder="Enter review findings..." />
          </Form.Item>

          <Form.Item name="compliance_issues" label="Compliance Issues">
            <TextArea rows={3} placeholder="List any compliance issues..." />
          </Form.Item>

          <Form.Item name="corrective_actions_required" label="Corrective Actions Required">
            <TextArea rows={3} placeholder="List required corrective actions..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Schedule Inspection Modal */}
      <Modal
        title="Schedule Witness Inspection"
        open={inspectionModalVisible}
        onCancel={() => {
          setInspectionModalVisible(false);
          inspectionForm.resetFields();
        }}
        onOk={() => inspectionForm.submit()}
      >
        <Form form={inspectionForm} layout="vertical" onFinish={handleScheduleInspection}>
          <Form.Item
            name="inspection_type"
            label="Inspection Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select>
              <Option value="Pre-Process">Pre-Process</Option>
              <Option value="In-Process">In-Process</Option>
              <Option value="Final">Final</Option>
              <Option value="Witness">Witness</Option>
            </Select>
          </Form.Item>

          <Form.Item name="inspection_stage" label="Inspection Stage">
            <Input placeholder="e.g., After Heat Treatment" />
          </Form.Item>

          <Form.Item
            name="inspection_date"
            label="Inspection Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="inspector_name"
            label="Inspector Name"
            rules={[{ required: true, message: 'Please enter inspector name' }]}
          >
            <Input placeholder="Inspector Name" />
          </Form.Item>

          <Form.Item name="inspection_scope" label="Inspection Scope">
            <TextArea rows={3} placeholder="Describe inspection scope..." />
          </Form.Item>

          <Form.Item name="acceptance_criteria" label="Acceptance Criteria">
            <TextArea rows={3} placeholder="Define acceptance criteria..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Record Inspection Results Modal */}
      <Modal
        title="Record Inspection Results"
        open={recordInspectionModalVisible}
        onCancel={() => {
          setRecordInspectionModalVisible(false);
          recordInspectionForm.resetFields();
        }}
        onOk={() => {
          const inspectionId = recordInspectionForm.getFieldValue('inspectionId');
          recordInspectionForm.validateFields().then(values => {
            handleRecordInspectionResults(values, inspectionId);
          });
        }}
      >
        <Form form={recordInspectionForm} layout="vertical">
          <Form.Item name="inspectionId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="inspection_result"
            label="Inspection Result"
            rules={[{ required: true, message: 'Please select result' }]}
          >
            <Radio.Group>
              <Radio value="Accepted">Accepted</Radio>
              <Radio value="Rejected">Rejected</Radio>
              <Radio value="Hold">Hold</Radio>
              <Radio value="Conditional">Conditional</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="findings" label="Findings">
            <TextArea rows={4} placeholder="Enter inspection findings..." />
          </Form.Item>

          <Form.Item name="non_conformances" label="Non-Conformances">
            <TextArea rows={3} placeholder="List any non-conformances..." />
          </Form.Item>

          <Form.Item name="observations" label="Observations">
            <TextArea rows={3} placeholder="Additional observations..." />
          </Form.Item>

          <Form.Item name="supplier_representative" label="Supplier Representative">
            <Input placeholder="Name of supplier representative present" />
          </Form.Item>

          <Form.Item name="inspection_report_url" label="Inspection Report URL">
            <Input placeholder="URL to inspection report" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OutsourcingManagementPage;
